import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { usePermissions, useNotify, Title } from 'react-admin';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import KeyIcon from '@mui/icons-material/Key';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Firestore path: settings/github
// Fields: owner, repo, branch, pat
//
// IMPORTANT — Firestore security rules:
// Lock this document to role-99 users only:
//
//   match /settings/github {
//     allow read, write: if request.auth != null
//       && get(/databases/$(database)/documents/users/$(request.auth.uid)).data.role >= 99;
//   }

const SETTINGS_REF = doc(db, 'settings', 'github');
const MASKED = '••••••••••••••••••••';
const WORKFLOW_FILE = 'deploy.yml';
const WORKFLOW_REF = 'main';

const STATUS_ICON = {
	completed_success: <CheckCircleIcon fontSize="small" color="success" />,
	completed_failure: <ErrorIcon fontSize="small" color="error" />,
	in_progress: <HourglassEmptyIcon fontSize="small" color="warning" />,
	queued: <HourglassEmptyIcon fontSize="small" color="warning" />,
};

async function fetchLastRun(pat, owner, repo) {
	const res = await fetch(
		`https://api.github.com/repos/${owner}/${repo}/actions/workflows/${WORKFLOW_FILE}/runs?per_page=1`,
		{
			headers: {
				Authorization: `Bearer ${pat}`,
				Accept: 'application/vnd.github+json',
				'X-GitHub-Api-Version': '2022-11-28',
			},
		}
	);
	if (!res.ok) throw new Error(res.status);
	const data = await res.json();
	return data.workflow_runs?.[0] ?? null;
}

async function triggerDeploy(pat, owner, repo) {
	const res = await fetch(
		`https://api.github.com/repos/${owner}/${repo}/actions/workflows/${WORKFLOW_FILE}/dispatches`,
		{
			method: 'POST',
			headers: {
				Authorization: `Bearer ${pat}`,
				Accept: 'application/vnd.github+json',
				'Content-Type': 'application/json',
				'X-GitHub-Api-Version': '2022-11-28',
			},
			body: JSON.stringify({ ref: WORKFLOW_REF }),
		}
	);
	if (!res.ok) {
		const text = await res.text();
		throw new Error(`GitHub ${res.status}: ${text}`);
	}
}

const GithubSettings = () => {
	const { permissions } = usePermissions();
	const notify = useNotify();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [hasSavedPat, setHasSavedPat] = useState(false);
	const [deploying, setDeploying] = useState(false);
	const [lastRun, setLastRun] = useState(null);
	const [runLoading, setRunLoading] = useState(false);
	const [savedPat, setSavedPat] = useState('');
	const [savedOwner, setSavedOwner] = useState('giomengu');
	const [savedRepo, setSavedRepo] = useState('giomengu.github.io');
	const [fields, setFields] = useState({
		owner: 'giomengu',
		repo: 'giomengu.github.io',
		branch: 'content',
		pat: '',
	});

	useEffect(() => {
		getDoc(SETTINGS_REF)
			.then(snap => {
				if (snap.exists()) {
					const d = snap.data();
					setFields(prev => ({
						...prev,
						owner: d.owner ?? prev.owner,
						repo: d.repo ?? prev.repo,
						branch: d.branch ?? prev.branch,
					}));
					if (d.pat) {
						setHasSavedPat(true);
						setSavedPat(d.pat);
						setSavedOwner(d.owner ?? 'giomengu');
						setSavedRepo(d.repo ?? 'giomengu.github.io');
					}
				}
			})
			.catch(e => notify(`Could not load settings: ${e.message}`, { type: 'error' }))
			.finally(() => setLoading(false));
	}, [notify]);

	const set = (key, value) => setFields(prev => ({ ...prev, [key]: value }));

	const loadLastRun = useCallback(async (pat, owner, repo) => {
		if (!pat) return;
		setRunLoading(true);
		try {
			const run = await fetchLastRun(pat, owner, repo);
			setLastRun(run);
		} catch {
			// silently ignore — PAT may not have Actions scope yet
		} finally {
			setRunLoading(false);
		}
	}, []);

	useEffect(() => {
		if (savedPat) loadLastRun(savedPat, savedOwner, savedRepo);
	}, [savedPat, savedOwner, savedRepo, loadLastRun]);

	const handleDeploy = async () => {
		if (!savedPat) { notify('Save settings first.', { type: 'warning' }); return; }
		setDeploying(true);
		try {
			await triggerDeploy(savedPat, savedOwner, savedRepo);
			notify('Deploy triggered — build will take ~2 minutes.', { type: 'success' });
			// Wait a moment then refresh status
			setTimeout(() => loadLastRun(savedPat, savedOwner, savedRepo), 3000);
		} catch (e) {
			notify(`Deploy failed: ${e.message}`, { type: 'error' });
		}
		setDeploying(false);
	};

	const handleSave = async () => {
		if (!fields.pat) {
			notify(
				hasSavedPat
					? 'A token is already saved. Enter a new one to replace it.'
					: 'Enter a GitHub PAT first.',
				{ type: 'warning' }
			);
			return;
		}
		setSaving(true);
		try {
			// pat is stored as plaintext — protect it with the Firestore rule above.
			await setDoc(SETTINGS_REF, {
				owner: fields.owner.trim(),
				repo: fields.repo.trim(),
				branch: fields.branch.trim(),
				pat: fields.pat,
			});
			setHasSavedPat(true);
			setSavedPat(fields.pat);
			setSavedOwner(fields.owner.trim());
			setSavedRepo(fields.repo.trim());
			setFields(prev => ({ ...prev, pat: '' }));
			notify('Settings saved.', { type: 'success' });
		} catch (e) {
			notify(`Save failed: ${e.message}`, { type: 'error' });
		}
		setSaving(false);
	};

	if (permissions < 99) {
		return (
			<Box p={4}>
				<Typography color="error">Admin access required (role 99).</Typography>
			</Box>
		);
	}

	return (
		<Box p={3} maxWidth={640}>
			<Title title="GitHub Content Settings" />

			<Box display="flex" alignItems="center" gap={1} mb={2}>
				<GitHubIcon />
				<Typography variant="h6">GitHub Content Settings</Typography>
			</Box>

			<Alert severity="warning" sx={{ mb: 3 }}>
				<strong>Security checklist before saving:</strong>
				<ol style={{ margin: '6px 0 0 16px', padding: 0 }}>
					<li>
						Create a <strong>fine-grained PAT</strong> scoped to{' '}
						<code>{fields.owner}/{fields.repo}</code> only, with{' '}
						<em>Repository contents: Read and write</em> and{' '}
						<em>Actions: Read and write</em> (needed for the Deploy button).
					</li>
					<li>
						Add this Firestore security rule so only role-99 admins can read the
						token:
						<pre style={{ fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
{`match /settings/github {
  allow read, write: if request.auth != null
    && get(/databases/$(database)/documents/users/$(request.auth.uid)).data.role >= 99;
}`}
						</pre>
					</li>
				</ol>
			</Alert>

			<Card>
				<CardContent>
					{loading ? (
						<CircularProgress />
					) : (
						<Box display="flex" flexDirection="column" gap={2}>
							<TextField
								label="Repository owner"
								value={fields.owner}
								onChange={e => set('owner', e.target.value)}
								fullWidth
								size="small"
							/>
							<TextField
								label="Repository name"
								value={fields.repo}
								onChange={e => set('repo', e.target.value)}
								fullWidth
								size="small"
							/>
							<TextField
								label="Content branch"
								value={fields.branch}
								onChange={e => set('branch', e.target.value)}
								fullWidth
								size="small"
							/>
							<TextField
								label={hasSavedPat ? 'New PAT (leave blank to keep existing)' : 'GitHub fine-grained PAT'}
								type="password"
								placeholder={hasSavedPat ? MASKED : 'ghp_...'}
								value={fields.pat}
								onChange={e => set('pat', e.target.value)}
								fullWidth
								size="small"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<KeyIcon fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
							<Box display="flex" gap={1} alignItems="center">
								<Button
									variant="contained"
									onClick={handleSave}
									disabled={saving}
									startIcon={<GitHubIcon />}
								>
									{saving ? 'Saving…' : 'Save settings'}
								</Button>
								{hasSavedPat && (
									<Typography variant="caption" color="success.main">
										Token saved ✓
									</Typography>
								)}
							</Box>
						</Box>
					)}
				</CardContent>
			</Card>

			{/* Deploy */}
			<Box display="flex" alignItems="center" gap={1} mt={4} mb={2}>
				<RocketLaunchIcon />
				<Typography variant="h6">Deploy</Typography>
			</Box>

			<Card>
				<CardContent>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Triggers the <code>deploy.yml</code> GitHub Actions workflow on the{' '}
						<code>main</code> branch — builds the React app and publishes to{' '}
						<code>gh-pages</code>. Takes ~2 minutes.
					</Typography>

					<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
						<Button
							variant="contained"
							color="secondary"
							startIcon={deploying ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
							onClick={handleDeploy}
							disabled={deploying || !hasSavedPat}
						>
							{deploying ? 'Triggering…' : 'Deploy now'}
						</Button>

						<Button
							variant="outlined"
							size="small"
							onClick={() => loadLastRun(savedPat, savedOwner, savedRepo)}
							disabled={runLoading || !hasSavedPat}
						>
							{runLoading ? 'Checking…' : 'Refresh status'}
						</Button>
					</Box>

					{lastRun && (
						<Box mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
							{STATUS_ICON[`${lastRun.status}_${lastRun.conclusion}`] ??
								STATUS_ICON[lastRun.status] ?? null}
							<Typography variant="body2">
								Last run:{' '}
								<strong>
									{lastRun.status === 'completed'
										? lastRun.conclusion
										: lastRun.status}
								</strong>
								{' · '}
								{new Date(lastRun.created_at).toLocaleString()}
							</Typography>
							<Chip
								label="View on GitHub"
								size="small"
								component="a"
								href={lastRun.html_url}
								target="_blank"
								rel="noopener noreferrer"
								clickable
							/>
						</Box>
					)}

					{!hasSavedPat && (
						<Typography variant="caption" color="text.secondary" mt={1} display="block">
							Save a PAT with <em>Actions: write</em> permission above to enable deploy.
						</Typography>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};

export default GithubSettings;
